import type { LicitacionEstado } from "@/types";

/** Etapas del pipeline que implican trabajo activo sobre una oferta vigente. */
export const ETAPAS_ACTIVAS: LicitacionEstado[] = [
  "analisis",
  "viable",
  "preparacion",
  "postulada",
];

export type EstadoElegibilidad =
  | "abierta"
  | "por-vencer"
  | "plazo-vencido"
  | "cerrada"
  | "adjudicada";

export interface Elegibilidad {
  estado: EstadoElegibilidad;
  /** true solo si aún es posible presentar una oferta. */
  postulable: boolean;
  /** Mensaje corto para tooltips/banners. */
  motivo: string;
  /** Horas hasta el cierre (null si no hay fecha válida). */
  horasRestantes: number | null;
}

// Incluye tanto el vocabulario de Licitación (estadoMP) como el de Compra
// Ágil (estadoCodigo) — el pipeline CRM comparte esta misma función para
// ambos tipos, así que el set cubre los estados "cerrados" de los dos.
const ESTADOS_MP_CERRADOS = new Set(["cerrada", "desierta", "revocada", "suspendida", "cancelada"]);
const HORAS_POR_VENCER = 48;

function horasHasta(fecha: string | null | undefined): number | null {
  if (!fecha) return null;
  const ms = new Date(fecha).getTime();
  if (Number.isNaN(ms)) return null;
  return (ms - Date.now()) / 3_600_000;
}

function formatCierre(fecha: string): string {
  const date = new Date(fecha);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("es-CL", { dateStyle: "medium", timeStyle: "short" }).format(date);
}

/**
 * Determina si una licitación admite ofertas. La fecha de cierre manda:
 * si ya venció, no es postulable aunque Mercado Público siga diciendo "Publicada".
 */
export function evaluarElegibilidad(licitacion: {
  estadoMP?: string | null;
  fechaCierre?: string | null;
}): Elegibilidad {
  const estadoMP = (licitacion.estadoMP ?? "").trim().toLowerCase();
  const horasRestantes = horasHasta(licitacion.fechaCierre);

  // "adjudicada" (Licitación) y "proveedor_seleccionado" (Compra Ágil) son
  // el mismo hecho: ya se eligió ganador, no admite más ofertas.
  if (estadoMP === "adjudicada" || estadoMP === "proveedor_seleccionado") {
    return {
      estado: "adjudicada",
      postulable: false,
      motivo: "Proceso adjudicado: ya no admite ofertas.",
      horasRestantes,
    };
  }

  if (ESTADOS_MP_CERRADOS.has(estadoMP)) {
    return {
      estado: "cerrada",
      postulable: false,
      motivo: `Estado en Mercado Público: ${estadoMP}. No admite ofertas.`,
      horasRestantes,
    };
  }

  if (horasRestantes !== null && horasRestantes <= 0) {
    return {
      estado: "plazo-vencido",
      postulable: false,
      motivo: `Cierre vencido el ${formatCierre(licitacion.fechaCierre as string)}: no admite ofertas.`,
      horasRestantes,
    };
  }

  if (horasRestantes !== null && horasRestantes <= HORAS_POR_VENCER) {
    return {
      estado: "por-vencer",
      postulable: true,
      motivo: `Cierra en ${Math.max(1, Math.round(horasRestantes))} h (${formatCierre(licitacion.fechaCierre as string)}).`,
      horasRestantes,
    };
  }

  return {
    estado: "abierta",
    postulable: true,
    motivo: licitacion.fechaCierre
      ? `Abierta hasta el ${formatCierre(licitacion.fechaCierre)}.`
      : "Abierta: sin fecha de cierre informada.",
    horasRestantes,
  };
}

/** Sufijo para el badge de estado MP en la tabla. */
export function etiquetaPlazo(elegibilidad: Elegibilidad): string | null {
  if (elegibilidad.estado === "plazo-vencido") return "Plazo vencido";
  if (elegibilidad.estado === "por-vencer") return "Por vencer";
  return null;
}

/** Una etapa activa exige que la licitación siga admitiendo ofertas. */
export function puedeMoverAEtapa(
  licitacion: { estadoMP?: string | null; fechaCierre?: string | null },
  etapa: LicitacionEstado,
): { permitido: boolean; motivo?: string } {
  if (!ETAPAS_ACTIVAS.includes(etapa)) return { permitido: true };
  const elegibilidad = evaluarElegibilidad(licitacion);
  if (elegibilidad.postulable) return { permitido: true };
  return { permitido: false, motivo: elegibilidad.motivo };
}
